import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap'
import './Menu.module.scss'

const propTypes = {
  /**
   * Options is an array composed of label, onClick and display.
   * onClick is a callback for when the option is clicked and pass the following arguments: row
   * display is a callback to show/hide the option and pass following arguments: row
   * disableLabel is the tooltip of a disabled option (node or row => node). Falls back to the label when empty.
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      onClick: PropTypes.func.isRequired,
      display: PropTypes.func,
      disableLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
      isDisabled: PropTypes.func,
    }),
  ),
  /**
   * Row where the menu is in
   */
  row: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * If true, menu will be disabled
   */
  loading: PropTypes.bool,
  /**
   * Dictates whether to show the menu for each row based on the row values, pass following arguments: row.
   */
  showMenu: PropTypes.func,
  /**
   * Optional custom toggle content (node or row => node). When set, replaces the ⋯ kebab.
   * If `showMenu` hides the menu for a row, only this toggle content is rendered (no dropdown).
   */
  toggle: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
}

const renderLabel = (label, row) => {
  if (typeof label === 'function') {
    return label(row)
  }
  return label
}

const renderDisableLabel = (option, row) => {
  const disableLabel = typeof option.disableLabel === 'function' ? option.disableLabel(row) : option.disableLabel

  return disableLabel || renderLabel(option.label, row)
}

const renderToggle = (toggle, row) => {
  if (typeof toggle === 'function') {
    return toggle(row)
  }
  return toggle
}

/**
 * Row menus stopPropagation so table row clicks do not fire. That also blocks Bootstrap's
 * document-level rootClose when opening another menu, so multiple menus can stay open.
 * Keep a single closer callback so only one Table Menu is open at a time.
 *
 * Closers remount the Dropdown (via key) instead of using controlled `show` — controlled mode
 * fights toggle + rootClose and reopens the menu on the same click that tried to close it.
 */
let closeActiveMenu = null

// Table wrapper scrolls horizontally; a fixed popper keeps the menu from being clipped.
const MENU_POPPER_CONFIG = { strategy: 'fixed' }

const CustomToggle = React.forwardRef(({ children, onClick, disabled, className, ...toggleProps }, ref) => (
  <div
    {...toggleProps}
    role="button"
    tabIndex={disabled ? -1 : 0}
    ref={ref}
    className={clsx('Uic-Menu-customToggle', className)}
    aria-disabled={disabled || undefined}
    onClick={(event) => {
      if (disabled) {
        return
      }
      event.preventDefault()
      onClick(event)
    }}
    onKeyDown={(event) => {
      if (disabled) {
        return
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onClick(event)
      }
    }}
  >
    {children}
  </div>
))

CustomToggle.displayName = 'MenuCustomToggle'
CustomToggle.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
}

function Menu(props) {
  const { options = [], row = null, loading = false, showMenu = undefined, toggle = undefined } = props ?? {}
  const [closeNonce, setCloseNonce] = useState(0)
  const toggleContent = toggle != null ? renderToggle(toggle, row) : null
  const menuVisible = typeof showMenu !== 'function' || showMenu(row)

  const closeMenu = useCallback(() => {
    setCloseNonce((nonce) => nonce + 1)
  }, [])

  useEffect(
    () => () => {
      if (closeActiveMenu === closeMenu) {
        closeActiveMenu = null
      }
    },
    [closeMenu],
  )

  const handleToggle = useCallback(
    (nextShow) => {
      if (nextShow) {
        if (closeActiveMenu && closeActiveMenu !== closeMenu) {
          closeActiveMenu()
        }
        closeActiveMenu = closeMenu
        return
      }

      if (closeActiveMenu === closeMenu) {
        closeActiveMenu = null
      }
    },
    [closeMenu],
  )

  if (!menuVisible) {
    // Custom toggle still renders as static content when the menu is unavailable for the row.
    return toggleContent
  }

  const hasCustomToggle = toggle != null

  return (
    <Dropdown
      key={closeNonce}
      onToggle={handleToggle}
      className={clsx('Uic-Menu-dropdownMenu', hasCustomToggle && 'Uic-Menu-dropdownMenu--customToggle')}
      onClick={(event) => event.stopPropagation()}
    >
      {hasCustomToggle ? (
        <Dropdown.Toggle as={CustomToggle} disabled={loading}>
          {toggleContent}
        </Dropdown.Toggle>
      ) : (
        <Dropdown.Toggle variant="link" className="Uic-Menu-dropdownToggle" disabled={loading}>
          <span className="fas fa-ellipsis-vertical" />
        </Dropdown.Toggle>
      )}
      <Dropdown.Menu popperConfig={MENU_POPPER_CONFIG}>
        {options
          .filter((option) => (option.display ? option.display(row) : true))
          .map((option, index) =>
            option?.isDisabled && option?.isDisabled(row) ? (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="Uic-Menu-dropdownPadding">
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip id="button-tooltip">{renderDisableLabel(option, row)}</Tooltip>}
                >
                  <div>
                    <Dropdown.Item className="Uic-Menu-dropdownItem" disabled>
                      {renderLabel(option.label, row)}
                    </Dropdown.Item>
                  </div>
                </OverlayTrigger>
              </div>
            ) : (
              <Dropdown.Item
                key={index} // eslint-disable-line react/no-array-index-key
                className="Uic-Menu-dropdownItem"
                onClick={() => option.onClick(row)}
              >
                {renderLabel(option.label, row)}
              </Dropdown.Item>
            ),
          )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

Menu.propTypes = propTypes

export default Menu
